/**
 * Students information modal.
 *
 * Collects one student record per seat and writes it onto the cart line items
 * as line item properties. The Shopify → Keap connector reads those
 * properties, so the keys below are a contract with it:
 *
 *   Buyer's own seat (buyer attending, seat 1):
 *     Student: "Buyer", Student email, First name, Last name, Date of birth,
 *     Phone (only if entered)
 *
 *   Every other seat:
 *     Email, First name, Last name, Date of birth, Phone (only if entered)
 *     Shippable kit lines also carry Shipping address (or "Buyer"), Zip code,
 *     City, State.
 *
 * `Email` — exact key — is what the connector uses to detect a gift seat and
 * open a separate Keap order for that student. The buyer's own seat must never
 * carry `Email`, or the buyer would get a second Keap order. It carries
 * `Student email` instead, which the connector reads only to resolve the Keap
 * contact for the master order. The `Student: "Buyer"` marker is unchanged.
 */

const ATTENDANCE = {
  SELF: "self",
  SELF_OTHERS: "self_others",
  OTHERS: "others",
};

class StudentsInformation extends HTMLElement {
  constructor() {
    super();
    this.modal = this.querySelector("#modal");
    this.modalContent = this.modal.querySelector(".modal-content");
    this.closeModalButton = this.querySelector("#closeModal");
    this.template = this.querySelector("template");
    this.atcButton = document.querySelector("[data-add-to-cart]");
    this.attachedProducts = this.dataset.attachedProductsId || null;
    this.quantitySelector = document.querySelector('select[name="quantity"]');
    this.bundleProduct = document.querySelector("[data-bundle-product]") || null;
    this.attendance = ATTENDANCE.SELF;
    this.buyerWillAttend = true;
    this.form = null;
  }

  connectedCallback() {
    if (!this.atcButton || !this.quantitySelector) return;

    this.atcButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.getData();
      this.setAttribute("open", "");
    });

    this.closeModalButton.addEventListener("click", () => {
      this.closeModal();
    });
  }

  closeModal() {
    this.modalContent.innerHTML = "";
    this.form = null;
    this.removeAttribute("open");
    // Add a small delay to ensure modal is fully closed before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 300); // Adjust delay if needed based on any CSS transitions
  }

  getData() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const variantId = urlParams.get("variant") || this.dataset.variantId;
    const quantity = Number(this.quantitySelector.value);

    this.renderForm(quantity, variantId);
  }

  /* ------------------------------------------------------------------ */
  /* State helpers                                                       */
  /* ------------------------------------------------------------------ */

  get quantity() {
    return this.form
      ? Number(this.form.dataset.quantity)
      : Number(this.quantitySelector.value);
  }

  get bundleProductSelected() {
    return Boolean(
      this.bundleProduct?.querySelector('input[value="true"]')?.checked
    );
  }

  get hasShippableItems() {
    return Boolean(this.attachedProducts || this.bundleProductSelected);
  }

  quantityOption(value) {
    return this.quantitySelector.querySelector(`option[value="${value}"]`);
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  renderForm(quantity, variantId) {
    this.modalContent.innerHTML = "";
    const template = this.template.content.cloneNode(true);
    const form = template.getElementById("recipientForm");
    this.form = form;

    form.addEventListener("submit", (e) => this.addToCart(e));
    form.setAttribute("data-variant-id", variantId);
    form.setAttribute("data-quantity", quantity);

    if (this.hasShippableItems) {
      form.querySelector(".shipping-notice").classList.remove("hide");
    }

    for (let i = 0; i < quantity; i++) {
      this.appendSeat(i);
    }

    this.modalContent.appendChild(form);

    // "Just me" only exists for a single seat; default to the matching option.
    this.attendance =
      quantity > 1 ? ATTENDANCE.SELF_OTHERS : ATTENDANCE.SELF;
    const attendanceRadio = form.querySelector(
      `input[name="attendance"][value="${this.attendance}"]`
    );
    if (attendanceRadio) attendanceRadio.checked = true;

    this.bindEvents();
    this.applyAttendance();
    this.applyPayment();
    this.initAutocomplete(form);
  }

  appendSeat(index) {
    const fragment = this.template.content.cloneNode(true);
    const seat = fragment.querySelector(".recipient-fields");
    seat.setAttribute("data-fields-index", index);
    this.form.querySelector(".form-fields").appendChild(seat);
    return seat;
  }

  bindEvents() {
    const form = this.form;

    form.querySelectorAll('input[name="attendance"]').forEach((radio) =>
      radio.addEventListener("change", (e) => {
        this.attendance = e.target.value;
        this.applyAttendance();
      })
    );

    form.querySelectorAll('input[name="payment"]').forEach((radio) =>
      radio.addEventListener("change", () => this.applyPayment())
    );

    form.querySelectorAll('input[name="shipping"]').forEach((radio) =>
      radio.addEventListener("change", () => this.applyShippingMode())
    );

    form
      .querySelector("[data-add-student]")
      ?.addEventListener("click", () => this.addStudent());

    form.addEventListener("click", (e) => {
      const button = e.target.closest("[data-remove-student]");
      if (button) this.removeStudent(button.closest(".recipient-fields"));
    });
  }

  /**
   * Applies the "who is attending" answer to the form: which seat is the
   * buyer's, whether "Just me" is available, gift copy, per-seat shipping and
   * the add-student affordance.
   */
  applyAttendance() {
    const form = this.form;
    const quantity = this.quantity;

    // "Just me" is unavailable above one seat.
    const justMe = form.querySelector('input[name="attendance"][value="self"]');
    const justMeUnavailable = quantity > 1;
    justMe.disabled = justMeUnavailable;
    justMe.closest(".inline-input").classList.toggle("is-disabled", justMeUnavailable);
    form.querySelector("[data-just-me-hint]").hidden = !justMeUnavailable;

    if (justMeUnavailable && this.attendance === ATTENDANCE.SELF) {
      this.attendance = ATTENDANCE.SELF_OTHERS;
      form.querySelector(
        'input[name="attendance"][value="self_others"]'
      ).checked = true;
    }

    this.buyerWillAttend = this.attendance !== ATTENDANCE.OTHERS;

    // Gift note only when somebody other than the buyer is being enrolled.
    form.querySelector("[data-gift-note]").hidden =
      this.attendance === ATTENDANCE.SELF;

    // Seat 1 is the buyer's on the self-attending paths.
    form.querySelectorAll(".recipient-fields").forEach((seat) => {
      const index = Number(seat.dataset.fieldsIndex);
      const isBuyer = this.buyerWillAttend && index === 0;

      seat.classList.toggle("is-buyer", isBuyer);
      seat.querySelector("[data-title]").innerText = `Student ${index + 1}${
        isBuyer ? " (You)" : ""
      }`;
      // The buyer's own seat can't be removed (the next seat would become
      // "You"), and the last remaining seat can't be removed either.
      seat.querySelector("[data-remove-student]").hidden =
        isBuyer || quantity <= 1;
      seat.querySelector('[data-email-note="buyer"]').hidden = !isBuyer;
      seat.querySelector('[data-email-note="student"]').hidden = isBuyer;
    });

    // Per-student shipping addresses are only offered when a kit ships and at
    // least one seat belongs to somebody other than the buyer. The buyer's own
    // kit always ships to the address entered at checkout.
    const showAddressSelector =
      this.hasShippableItems && (quantity > 1 || !this.buyerWillAttend);
    form
      .querySelector(".multiple-addresses-selector")
      .classList.toggle("hide", !showAddressSelector);
    if (!showAddressSelector) {
      form.querySelector(
        'input[name="shipping"][value="same_address"]'
      ).checked = true;
    }
    this.applyShippingMode();

    // Offer another seat while the quantity selector has room for one.
    form.querySelector("[data-add-student]").hidden = !this.quantityOption(
      quantity + 1
    );
  }

  applyShippingMode() {
    const different = this.form.querySelector(
      'input[name="shipping"][value="different_address"]'
    )?.checked;

    this.form
      .querySelectorAll(".differentAdressForm")
      .forEach((f) => f.classList.toggle("hide", !different));
  }

  /**
   * The payment question is clarity copy only. It is never written to the cart
   * and nothing downstream reads it.
   */
  applyPayment() {
    const value =
      this.form.querySelector('input[name="payment"]:checked')?.value || "own";

    this.form.querySelectorAll("[data-pay-note]").forEach((note) => {
      note.hidden = note.dataset.payNote !== value;
    });
  }

  /**
   * Adds a seat from inside the modal and keeps the product page's quantity
   * selector in sync, so pricing tiers and the cart line count agree with what
   * the modal collects.
   */
  addStudent() {
    const next = this.quantity + 1;
    if (!this.quantityOption(next)) return;

    this.quantitySelector.value = String(next);
    this.quantitySelector.dispatchEvent(new Event("change", { bubbles: true }));
    this.form.setAttribute("data-quantity", next);

    const seat = this.appendSeat(next - 1);
    this.applyAttendance();
    this.initAutocomplete(seat);

    seat.scrollIntoView({ block: "start", behavior: "smooth" });
    seat.querySelector('input[name="email"]')?.focus({ preventScroll: true });
  }

  /**
   * Removes a seat from inside the modal, renumbers the rest and steps the
   * product page's quantity selector down to match. The buyer's own seat and
   * the last remaining seat are never removable (their links are hidden).
   */
  removeStudent(seat) {
    if (!seat || seat.classList.contains("is-buyer") || this.quantity <= 1) {
      return;
    }

    seat.remove();

    const seats = this.form.querySelectorAll(".recipient-fields");
    seats.forEach((s, index) => s.setAttribute("data-fields-index", index));

    const next = seats.length;
    this.quantitySelector.value = String(next);
    this.quantitySelector.dispatchEvent(new Event("change", { bubbles: true }));
    this.form.setAttribute("data-quantity", next);

    // Down to a single seat with "Me and others" selected means the only
    // student left is the buyer, which is "Just me".
    if (next === 1 && this.attendance === ATTENDANCE.SELF_OTHERS) {
      this.attendance = ATTENDANCE.SELF;
      this.form.querySelector(
        'input[name="attendance"][value="self"]'
      ).checked = true;
    }

    this.applyAttendance();
  }

  initAutocomplete(root = this.form) {
    if (!root) return;
    if (!(window.google && google.maps && google.maps.places)) return;

    // Initialize Google Maps Places Autocomplete for each shipping input
    const addressInputs = root.querySelectorAll(".shippingAddress");

    for (let addressInput of addressInputs) {
      const autocomplete = new google.maps.places.Autocomplete(addressInput, {
        types: ["address"],
        componentRestrictions: { country: "us" }, // Adjust as needed
        appendTo: this.modal,
      });

      // Listen for the 'place_changed' event for each input
      autocomplete.addListener("place_changed", function () {
        const place = autocomplete.getPlace();

        if (place.address_components) {
          // Parse the address components and fill the form fields within the same form
          const formFields = {
            street: "",
            city: "",
            state: "",
            zipCode: "",
          };

          place.address_components.forEach((component) => {
            const types = component.types;
            if (types.includes("street_number")) {
              formFields.street = `${component.long_name} ${formFields.street}`;
            }
            if (types.includes("route")) {
              formFields.street += component.long_name;
            }
            if (types.includes("locality")) {
              formFields.city = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              formFields.state = component.short_name;
            }
            if (types.includes("postal_code")) {
              formFields.zipCode = component.long_name;
            }
          });

          // Find the parent form and populate the corresponding fields
          const parentForm = addressInput.closest(".differentAdressForm");
          if (parentForm) {
            parentForm.querySelector(".shippingAddress").value =
              formFields.street;
            parentForm.querySelector(".city").value = formFields.city;
            parentForm.querySelector(".state").value = formFields.state;
            parentForm.querySelector(".zipCode").value = formFields.zipCode;
          }
        }
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Validation                                                          */
  /* ------------------------------------------------------------------ */

  setFieldError(input, message) {
    const field = input.closest(".field");
    field.classList.add("error");
    field.querySelector(".field__error").innerText = message;
    input.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  validate(form) {
    form.querySelectorAll(".field").forEach((f) => f.classList.remove("error"));
    form.querySelectorAll(".field__error").forEach((f) => (f.innerText = ""));
    this.showFormError("");

    const seats = form.querySelectorAll(".recipient-fields");
    const emailArray = [];

    for (let seat of seats) {
      const isBuyer = seat.classList.contains("is-buyer");

      const email = seat.querySelector('input[name="email"]');
      const firstName = seat.querySelector('input[name="first_name"]');
      const lastName = seat.querySelector('input[name="last_name"]');
      const phoneNumber = seat.querySelector('input[name="phone_number"]');
      const dob = seat.querySelector('input[name="dob"]');

      if (!firstName.value.trim()) {
        this.setFieldError(firstName, "Please enter a first name");
        return false;
      }

      if (!lastName.value.trim()) {
        this.setFieldError(lastName, "Please enter a last name");
        return false;
      }

      const emailValue = email.value.trim();

      if (!emailValue) {
        this.setFieldError(email, "Please enter an email address");
        return false;
      }

      if (!validators.validateEmail(emailValue)) {
        this.setFieldError(email, "Please enter a valid email address");
        return false;
      }

      if (emailArray.includes(emailValue.toLowerCase())) {
        this.setFieldError(email, "Email needs to be unique");
        return false;
      }
      emailArray.push(emailValue.toLowerCase());

      if (!dob.value) {
        this.setFieldError(dob, "Please enter a date of birth");
        return false;
      }

      if (!validators.validateDob(dob.value)) {
        this.setFieldError(dob, "You need to be at least 21 years old.");
        return false;
      }

      // Phone is required for every student other than the buyer, whose phone
      // is also collected at checkout.
      if (!isBuyer && !phoneNumber.value.trim()) {
        this.setFieldError(phoneNumber, "Please enter a phone number");
        return false;
      }

      // A bare 10-digit number (or 11 with a leading 1) is reformatted to
      // 123-456-7890 so a missing dash never blocks the form.
      const digits = phoneNumber.value.replace(/\D/g, "");
      if (digits.length === 10) {
        phoneNumber.value = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length === 11 && digits[0] === "1") {
        phoneNumber.value = `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
      }

      if (
        phoneNumber.value.trim() &&
        !validators.validatePhone(phoneNumber.value.trim())
      ) {
        this.setFieldError(
          phoneNumber,
          "Please enter a phone following the format 123-456-7890"
        );
        return false;
      }

      // Per-seat shipping address, only when the buyer asked for different
      // addresses. The buyer's own seat never has one.
      const differentAddressForm = seat.querySelector(".differentAdressForm");
      if (isBuyer || differentAddressForm.classList.contains("hide")) continue;

      const shippingAddress = seat.querySelector('input[name="shippingAddress"]');
      const zipCode = seat.querySelector('input[name="zipCode"]');
      const city = seat.querySelector('input[name="city"]');
      const state = seat.querySelector('input[name="state"]');

      if (!shippingAddress.value.trim()) {
        this.setFieldError(shippingAddress, "Please enter a valid address");
        return false;
      }

      if (!zipCode.value.trim()) {
        this.setFieldError(zipCode, "Please enter a valid Zip Code");
        return false;
      }

      if (!city.value.trim()) {
        this.setFieldError(city, "Please enter a valid city");
        return false;
      }

      if (!state.value.trim()) {
        this.setFieldError(state, "Please enter a valid state");
        return false;
      }
    }

    return true;
  }

  showFormError(message) {
    const el = this.form?.querySelector("[data-form-error]");
    if (!el) return;
    el.innerText = message;
    el.hidden = !message;
  }

  /* ------------------------------------------------------------------ */
  /* Line item properties                                                */
  /* ------------------------------------------------------------------ */

  seatValues(seat) {
    const read = (name) =>
      seat.querySelector(`input[name="${name}"]`)?.value.trim() ?? "";

    return {
      isBuyer: seat.classList.contains("is-buyer"),
      email: read("email"),
      firstName: read("first_name"),
      lastName: read("last_name"),
      dob: read("dob"),
      phone: read("phone_number"),
      shippingAddress: read("shippingAddress"),
      zipCode: read("zipCode"),
      city: read("city"),
      state: read("state"),
    };
  }

  /**
   * Properties for the buyer's own seat. Applied to the course line and to any
   * kit line for that seat. Deliberately has no `Email` key and no shipping
   * keys: the seat stays on the master Keap order, and the kit ships to the
   * checkout address.
   */
  buyerProperties(values) {
    const properties = {
      Student: "Buyer",
      "Student email": values.email,
      "First name": values.firstName,
      "Last name": values.lastName,
      "Date of birth": values.dob,
    };

    if (values.phone) properties["Phone"] = values.phone;

    return properties;
  }

  /** Properties for the course line of one seat. */
  courseProperties(seat) {
    const values = this.seatValues(seat);
    if (values.isBuyer) return this.buyerProperties(values);

    const properties = {
      Email: values.email,
      "First name": values.firstName,
      "Last name": values.lastName,
      "Date of birth": values.dob,
    };

    if (values.phone) properties["Phone"] = values.phone;

    return properties;
  }

  /** Properties for a shippable kit line (bundle or attached product) of one seat. */
  shippableProperties(seat) {
    const values = this.seatValues(seat);
    if (values.isBuyer) return this.buyerProperties(values);

    const properties = {
      Email: values.email,
      "First name": values.firstName,
      "Last name": values.lastName,
    };

    if (values.phone) properties["Phone"] = values.phone;

    properties["Shipping address"] = values.shippingAddress || "Buyer";
    if (values.zipCode) properties["Zip code"] = values.zipCode;
    if (values.city) properties["City"] = values.city;
    if (values.state) properties["State"] = values.state;

    return properties;
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                         */
  /* ------------------------------------------------------------------ */

  async addToCart(e) {
    e.preventDefault();

    const form = e.target;
    const variant = form.dataset.variantId;
    const seats = Array.from(form.querySelectorAll(".recipient-fields"));

    if (!this.validate(form)) return;

    const formData = { items: [] };

    // Course: one line per seat
    seats.forEach((seat) => {
      formData.items.push({
        id: variant,
        quantity: 1,
        properties: this.courseProperties(seat),
      });
    });

    // Bundle (wine kit) product: one line per seat
    if (this.bundleProduct && this.bundleProductSelected) {
      const bundleVariantId = this.bundleProduct.dataset.variantId;

      seats.forEach((seat) => {
        formData.items.push({
          id: bundleVariantId,
          quantity: 1,
          properties: this.shippableProperties(seat),
        });
      });
    }

    // Attached products: one line per seat per attached product
    if (this.attachedProducts) {
      const attachedProductsArr = this.attachedProducts.split(" , ");

      seats.forEach((seat) => {
        const properties = this.shippableProperties(seat);

        for (let attachedProduct of attachedProductsArr) {
          formData.items.push({
            id: attachedProduct,
            quantity: 1,
            properties,
          });
        }
      });
    }

    const submitButton = form.querySelector('button[name="add"]');
    submitButton.disabled = true;

    try {
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        let description = "Something went wrong adding this course to your cart.";
        try {
          const body = await response.json();
          description = body.description || body.message || description;
        } catch (_) {
          /* non-JSON error body */
        }
        throw new Error(description);
      }

      this.closeModal();
      document.dispatchEvent(new CustomEvent("cart:build"));

      // [SC] - 8th Oct 2025 - for redirect to direct checkout page on product addition on custom landing template
      if (typeof goToCheckout !== "function") {
        console.log("goToCheckout not defined");
      } else {
        goToCheckout();
      }

      document.getElementById("HeaderCartTrigger")?.click();
    } catch (error) {
      console.error("Error:", error);
      this.showFormError(error.message || "Something went wrong. Please try again.");
      submitButton.disabled = false;
    }
  }
}

customElements.define("students-information", StudentsInformation);

const validators = {
  validateEmail: (email) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  },

  validatePhone: (phone) => {
    const regex = /^(\([0-9]{3}\)\s*|[0-9]{3}\-)[0-9]{3}-[0-9]{4}$/;
    return regex.test(phone);
  },

  validateDob: (dob) => {
    const today = new Date();
    const dobDate = new Date(dob);

    const ageInMilliseconds = today - dobDate;
    const ageInYears = ageInMilliseconds / (365 * 24 * 60 * 60 * 1000);

    return ageInYears >= 21;
  },
};
